import { useTranslation } from 'react-i18next';

import { Button, Tag } from '@arco-design/web-react';
import { IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';

import { IconFont } from '@/components';

import { resolveUsageScenarioLabel } from './constants';
import styles from './index.module.less';
import cardStyles from './service-card.module.less';
import type { ServicePortCardProps } from './types';

interface ServiceMetric {
  key: string;
  label: string;
  value: string;
}

function buildServiceMetrics(item: ServicePortCardProps['item'], t: (key: string) => string): ServiceMetric[] {
  return [
    {
      key: 'usageScenario',
      label: t('data-center:usage.scenario'),
      value: resolveUsageScenarioLabel(item.usageScenario),
    },
    {
      key: 'internalPort',
      label: t('common:internal.port'),
      value: item.internalPort != null ? String(item.internalPort) : '-',
    },
    {
      key: 'routerPath',
      label: t('common:path'),
      value: item.routerPath || '-',
    },
  ];
}

/** 服务端口摘要卡片 */
export function ServicePortCard({ item, canEdit, canDelete, onEdit, onDelete }: ServicePortCardProps) {
  const { t } = useTranslation();
  const title = item.name?.trim() || t('data-center:maas.service.name');
  const metrics = buildServiceMetrics(item, t);
  const description = item.description?.trim();

  return (
    <div className={cardStyles.card}>
      <div className={cardStyles.cardMain}>
        <div className={cardStyles.cardIdentity}>
          <div className={cardStyles.cardIcon}>
            <IconFont name="icon-duankou" style={{ fontSize: 24 }} />
          </div>
          <div className={cardStyles.cardMeta}>
            <div className={cardStyles.cardTitleRow}>
              <span className={cardStyles.cardName} title={title}>
                {title}
              </span>
              {item._isDefault ? (
                <Tag size="small" color="arcoblue">
                  {t('data-center:maas.inference.service.image.builtin')}
                </Tag>
              ) : null}
            </div>
            <div className={styles.cardSubtitleLine} title={description}>
              {t('common:description')}: {description || '-'}
            </div>
          </div>
        </div>
        {canEdit || canDelete ? (
          <div className={cardStyles.cardActions}>
            {canEdit ? (
              <Button type="text" size="small" icon={<IconEdit />} onClick={onEdit}>
                {t('common:edit')}
              </Button>
            ) : null}
            {canDelete ? (
              <Button type="text" size="small" status="danger" icon={<IconDelete />} onClick={onDelete}>
                {t('common:delete')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={cardStyles.cardMetrics}>
        {metrics.map((metric) => (
          <div key={metric.key} className={cardStyles.metricItem} title={`${metric.label}: ${metric.value}`}>
            <span className={cardStyles.metricLabel}>{metric.label}</span>
            <span className={cardStyles.metricValue}>{metric.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ServicePortAddCardProps {
  onClick: () => void;
}

/** 添加服务端口（与 DNS 等能力块统一的 dashed 灰色按钮） */
export function ServicePortAddCard({ onClick }: ServicePortAddCardProps) {
  const { t } = useTranslation();

  return (
    <Button type="dashed" long icon={<IconPlus />} className={styles.addButton} onClick={onClick}>
      {t('data-center:maas.inference.service.add')}
    </Button>
  );
}
